import React, { useState, useEffect } from 'react';
import { 
  Settings, Percent, Euro, Package, Palette, Crown, 
  Save, Plus, Edit2, Trash2, Check, X, AlertCircle,
  TrendingUp, Users, DollarSign, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const VendorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Global Settings
  const [globalSettings, setGlobalSettings] = useState({
    default_commission_rate: 8.0,
    default_monthly_fee: 0.0,
    default_tier_id: 'basis',
    commission_payout_threshold: 50.0,
    payout_frequency: 'monthly',
    auto_approve_vendors: false,
    allow_vendor_registration: true
  });
  
  // Subscription Tiers
  const [tiers, setTiers] = useState([]);
  const [editingTier, setEditingTier] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  
  // Commission Overview
  const [commissions, setCommissions] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'commissions') {
      loadCommissions();
    }
  }, [activeTab, selectedYear, selectedMonth]);

  const loadData = async () => {
    try {
      const [settingsRes, tiersRes] = await Promise.all([
        axiosInstance.get('/vendor-settings/global'),
        axiosInstance.get('/vendor-settings/tiers')
      ]);
      setGlobalSettings(settingsRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const loadCommissions = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedYear) params.append('year', selectedYear);
      if (selectedMonth) params.append('month', selectedMonth);
      
      const response = await axiosInstance.get(`/vendor-settings/commissions?${params}`);
      setCommissions(response.data);
    } catch (error) {
      console.error('Error loading commissions:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/vendor-settings/global', globalSettings);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTier = async () => {
    if (!editingTier) return;
    
    try {
      if (editingTier.isNew) {
        await axiosInstance.post('/vendor-settings/tiers', editingTier);
        toast.success('Paket erstellt');
      } else {
        await axiosInstance.put(`/vendor-settings/tiers/${editingTier.id}`, editingTier);
        toast.success('Paket aktualisiert');
      }
      loadData();
      setShowTierDialog(false);
      setEditingTier(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm('Möchten Sie dieses Paket wirklich löschen?')) return;
    
    try {
      await axiosInstance.delete(`/vendor-settings/tiers/${tierId}`);
      toast.success('Paket gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleToggleTierActive = async (tier) => {
    const updated = { ...tier, is_active: !tier.is_active };
    try {
      await axiosInstance.put(`/vendor-settings/tiers/${tier.id}`, updated);
      toast.success(updated.is_active ? 'Paket aktiviert' : 'Paket deaktiviert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: 'Januar' }, { value: 2, label: 'Februar' },
    { value: 3, label: 'März' }, { value: 4, label: 'April' },
    { value: 5, label: 'Mai' }, { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' }, { value: 12, label: 'Dezember' }
  ];

  if (loading) {
    return <div className="p-8">Lade Einstellungen...</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      <AdminPageHeader 
        title="Marktplatz-Einstellungen"
        subtitle="Provisionen, Gebühren und Verkäufer-Pakete verwalten"
        icon={Settings}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md mb-6 bg-[#C4A084] p-1 rounded-none border-0 gap-1">
          <TabsTrigger value="settings" className="rounded-none border-0 bg-[#FFF8F0] hover:bg-[#FFEFE0] data-[state=active]:bg-white data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#E89860] py-2">Einstellungen</TabsTrigger>
          <TabsTrigger value="tiers" className="rounded-none border-0 bg-[#F0F8FF] hover:bg-[#E0F0FF] data-[state=active]:bg-white data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#60A0D0] py-2">Pakete</TabsTrigger>
          <TabsTrigger value="commissions" className="rounded-none border-0 bg-[#F0FFF0] hover:bg-[#E0FFE0] data-[state=active]:bg-white data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#60B060] py-2">Provisionen</TabsTrigger>
        </TabsList>

        {/* GLOBAL SETTINGS TAB */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Commission Settings */}
            <Card className="border-[#C4A084]">
              <CardHeader className="p-0">
                <div className="p-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
                  <div className="w-8 h-8 rounded flex items-center justify-center bg-[#F0FFF0]">
                    <Percent className="h-4 w-4 text-[#60B060]" />
                  </div>
                  <CardTitle className="text-white text-base">Provisions-Einstellungen</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Standard-Provision (%)</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Prozentsatz, den Sie von jedem Verkäufer-Verkauf erhalten
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={globalSettings.default_commission_rate}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        default_commission_rate: parseFloat(e.target.value) || 0
                      })}
                      className="w-24"
                    />
                    <span className="text-gray-600">%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Beispiel: Bei 8% und einem Verkauf von 100€ erhalten Sie 8€
                  </p>
                </div>

                <div>
                  <Label>Monatliche Gebühr (€)</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Feste monatliche Gebühr für jeden Verkäufer (0 = keine)
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.99"
                      value={globalSettings.default_monthly_fee}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        default_monthly_fee: parseFloat(e.target.value) || 0
                      })}
                      className="w-24"
                    />
                    <span className="text-gray-600">€ / Monat</span>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Label>Auszahlungs-Schwelle (€)</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Mindestbetrag, bevor eine Auszahlung an Verkäufer erfolgt
                  </p>
                  <Input
                    type="number"
                    min="0"
                    step="10"
                    value={globalSettings.commission_payout_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      commission_payout_threshold: parseFloat(e.target.value) || 0
                    })}
                    className="w-32"
                  />
                </div>

                <div>
                  <Label>Auszahlungs-Rhythmus</Label>
                  <Select
                    value={globalSettings.payout_frequency}
                    onValueChange={(value) => setGlobalSettings({
                      ...globalSettings,
                      payout_frequency: value
                    })}
                  >
                    <SelectTrigger className="w-48 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Wöchentlich</SelectItem>
                      <SelectItem value="bi-weekly">Alle 2 Wochen</SelectItem>
                      <SelectItem value="monthly">Monatlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Registration Settings */}
            <Card className="border-[#C4A084]">
              <CardHeader className="p-0">
                <div className="p-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
                  <div className="w-8 h-8 rounded flex items-center justify-center bg-[#F0F8FF]">
                    <Users className="h-4 w-4 text-[#60A0D0]" />
                  </div>
                  <CardTitle className="text-white text-base">Registrierungs-Einstellungen</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Verkäufer-Registrierung erlauben</Label>
                    <p className="text-sm text-gray-500">
                      Neue Verkäufer können sich anmelden
                    </p>
                  </div>
                  <Switch
                    checked={globalSettings.allow_vendor_registration}
                    onCheckedChange={(checked) => setGlobalSettings({
                      ...globalSettings,
                      allow_vendor_registration: checked
                    })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Automatische Freigabe</Label>
                    <p className="text-sm text-gray-500">
                      Verkäufer werden ohne Prüfung freigegeben
                    </p>
                  </div>
                  <Switch
                    checked={globalSettings.auto_approve_vendors}
                    onCheckedChange={(checked) => setGlobalSettings({
                      ...globalSettings,
                      auto_approve_vendors: checked
                    })}
                  />
                </div>

                <div className="pt-4 border-t">
                  <Label>Standard-Paket für neue Verkäufer</Label>
                  <Select
                    value={globalSettings.default_tier_id}
                    onValueChange={(value) => setGlobalSettings({
                      ...globalSettings,
                      default_tier_id: value
                    })}
                  >
                    <SelectTrigger className="w-48 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiers.filter(t => t.is_active).map(tier => (
                        <SelectItem key={tier.id} value={tier.id}>
                          {tier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#C4A084] hover:bg-[#A08060] text-white border-0">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichern...' : 'Einstellungen speichern'}
            </Button>
          </div>

          {/* Current Settings Summary */}
          <Card className="mt-6 border-[#C4A084]">
            <CardHeader className="p-0">
              <div className="p-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
                <div className="w-8 h-8 rounded flex items-center justify-center bg-[#F0FFF0]">
                  <Check className="h-4 w-4 text-[#60B060]" />
                </div>
                <CardTitle className="text-white text-base">Aktuelle Einstellungen</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-[#8B7355]">Provision:</span>
                  <p className="font-bold text-[#5D4E3C]">{globalSettings.default_commission_rate}%</p>
                </div>
                <div>
                  <span className="text-[#8B7355]">Monatliche Gebühr:</span>
                  <p className="font-bold text-[#5D4E3C]">{formatEUR(globalSettings.default_monthly_fee)}</p>
                </div>
                <div>
                  <span className="text-[#8B7355]">Auszahlungs-Schwelle:</span>
                  <p className="font-bold text-[#5D4E3C]">{formatEUR(globalSettings.commission_payout_threshold)}</p>
                </div>
                <div>
                  <span className="text-[#8B7355]">Auszahlung:</span>
                  <p className="font-bold text-[#5D4E3C]">
                    {globalSettings.payout_frequency === 'monthly' ? 'Monatlich' : 
                     globalSettings.payout_frequency === 'weekly' ? 'Wöchentlich' : 'Alle 2 Wochen'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUBSCRIPTION TIERS TAB */}
        <TabsContent value="tiers">
          {/* Goldbraune Überschrift für Pakete */}
          <div className="mb-4 p-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded flex items-center justify-center bg-[#F0F8FF]">
                <Package className="h-4 w-4 text-[#60A0D0]" />
              </div>
              <span className="text-white font-semibold">Verkäufer-Pakete</span>
            </div>
            <Button onClick={() => {
              setEditingTier({
                id: `tier-${Date.now()}`,
                name: '',
                description: '',
                commission_rate: 8.0,
                monthly_fee: 0,
                max_products: 50,
                features: [],
                can_customize_design: false,
                can_customize_colors: false,
                priority_support: false,
                is_active: false,
                order: tiers.length,
                isNew: true
              });
              setShowTierDialog(true);
            }} className="bg-[#FFF8F0] hover:bg-[#FFEFE0] text-[#E89860] border-[#C4A084]">
              <Plus className="h-4 w-4 mr-2" />
              Neues Paket
            </Button>
          </div>

          <p className="text-[#8B7355] mb-4">
            Erstellen Sie verschiedene Pakete mit unterschiedlichen Provisionen und Funktionen
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <Card 
                key={tier.id} 
                className={`relative border-[#C4A084] ${!tier.is_active && 'opacity-60'}`}
              >
                {tier.id === 'basis' && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-[#60A0D0]">Standard</Badge>
                  </div>
                )}
                <CardHeader className="p-0">
                  <div className="p-3 flex justify-between items-start" style={{ backgroundColor: tier.is_active ? '#C4A084' : '#E8DFD4' }}>
                    <div>
                      <CardTitle className="flex items-center gap-2 text-white">
                        {tier.name === 'Premium' && <Crown className="h-5 w-5 text-yellow-300" />}
                        {tier.name}
                      </CardTitle>
                      <p className="text-sm text-white/80 mt-1">{tier.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingTier({ ...tier });
                          setShowTierDialog(true);
                        }}
                        className="text-white hover:bg-white/20"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {tier.id !== 'basis' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTier(tier.id)}
                          className="text-white hover:bg-red-500/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Provision:</span>
                      <Badge variant="outline" className="text-lg font-bold">
                        {tier.commission_rate}%
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Monatlich:</span>
                      <span className="font-bold">
                        {tier.monthly_fee === 0 ? 'Kostenlos' : `${formatEUR(tier.monthly_fee)}`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Max. Produkte:</span>
                      <span className="font-bold">
                        {tier.max_products === -1 ? 'Unbegrenzt' : tier.max_products}
                      </span>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <p className="text-sm font-semibold mb-2">Funktionen:</p>
                      <ul className="text-sm space-y-1">
                        {tier.features?.slice(0, 4).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-3 border-t flex justify-between items-center">
                      <span className="text-sm text-gray-600">Aktiv:</span>
                      <Switch
                        checked={tier.is_active}
                        onCheckedChange={() => handleToggleTierActive(tier)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* COMMISSIONS TAB */}
        <TabsContent value="commissions">
          {/* Goldbraune Überschrift für Provisionen */}
          <div className="mb-4 p-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-8 h-8 rounded flex items-center justify-center bg-[#F0FFF0]">
              <TrendingUp className="h-4 w-4 text-[#60B060]" />
            </div>
            <span className="text-white font-semibold">Provisions-Übersicht</span>
          </div>

          <Card className="mb-6 border-[#C4A084]">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <Label className="text-[#5D4E3C]">Zeitraum:</Label>
                <Select value={selectedYear?.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24 border-[#C4A084]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={selectedMonth?.toString() || 'all'} 
                  onValueChange={(v) => setSelectedMonth(v === 'all' ? null : parseInt(v))}
                >
                  <SelectTrigger className="w-32 border-[#C4A084]">
                    <SelectValue placeholder="Alle Monate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Monate</SelectItem>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {commissions && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="border-[#C4A084]">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded flex items-center justify-center bg-[#F0F8FF]">
                      <DollarSign className="h-5 w-5 text-[#60A0D0]" />
                    </div>
                    <div className="text-2xl font-bold text-[#5D4E3C]">{formatEUR(commissions.totals.total_vendor_sales)}</div>
                    <div className="text-sm text-[#8B7355]">Verkäufer-Umsatz</div>
                  </CardContent>
                </Card>
                <Card className="border-[#C4A084]">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded flex items-center justify-center bg-[#F0FFF0]">
                      <TrendingUp className="h-5 w-5 text-[#60B060]" />
                    </div>
                    <div className="text-2xl font-bold text-[#60B060]">{formatEUR(commissions.totals.total_commission)}</div>
                    <div className="text-sm text-[#8B7355]">Ihre Provision</div>
                  </CardContent>
                </Card>
                <Card className="border-[#C4A084]">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded flex items-center justify-center bg-[#F8F0FF]">
                      <Euro className="h-5 w-5 text-[#A070D0]" />
                    </div>
                    <div className="text-2xl font-bold text-[#5D4E3C]">{formatEUR(commissions.totals.total_vendor_earnings)}</div>
                    <div className="text-sm text-[#8B7355]">Verkäufer-Einnahmen</div>
                  </CardContent>
                </Card>
                <Card className="border-[#C4A084]">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded flex items-center justify-center bg-[#FFF8F0]">
                      <Users className="h-5 w-5 text-[#E89860]" />
                    </div>
                    <div className="text-2xl font-bold text-[#5D4E3C]">{commissions.totals.vendor_count}</div>
                    <div className="text-sm text-[#8B7355]">Aktive Verkäufer</div>
                  </CardContent>
                </Card>
              </div>

              {/* Vendor Breakdown */}
              {commissions.vendors.length > 0 ? (
                <Card className="border-[#C4A084]">
                  <CardHeader className="p-0">
                    <div className="p-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
                      <div className="w-8 h-8 rounded flex items-center justify-center bg-[#FFF8F0]">
                        <Users className="h-4 w-4 text-[#E89860]" />
                      </div>
                      <CardTitle className="text-white text-base">Provisions-Übersicht nach Verkäufer</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Verkäufer</th>
                            <th className="text-right py-2">Bestellungen</th>
                            <th className="text-right py-2">Umsatz</th>
                            <th className="text-right py-2">Provision (%)</th>
                            <th className="text-right py-2">Ihre Provision</th>
                            <th className="text-right py-2">Verkäufer-Einnahmen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commissions.vendors.map((vendor) => (
                            <tr key={vendor.vendor_id} className="border-b hover:bg-gray-50">
                              <td className="py-2 font-medium">{vendor.vendor_name}</td>
                              <td className="text-right py-2">{vendor.order_count}</td>
                              <td className="text-right py-2">{formatEUR(vendor.total_sales)}</td>
                              <td className="text-right py-2">{vendor.commission_rate}%</td>
                              <td className="text-right py-2 text-green-600 font-semibold">
                                {formatEUR(vendor.total_commission)}
                              </td>
                              <td className="text-right py-2">{formatEUR(vendor.vendor_earnings)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-bold bg-gray-50">
                            <td className="py-2">Gesamt</td>
                            <td className="text-right py-2">-</td>
                            <td className="text-right py-2">{formatEUR(commissions.totals.total_vendor_sales)}</td>
                            <td className="text-right py-2">-</td>
                            <td className="text-right py-2 text-green-600">
                              {formatEUR(commissions.totals.total_commission)}
                            </td>
                            <td className="text-right py-2">{formatEUR(commissions.totals.total_vendor_earnings)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Noch keine Verkäufer-Verkäufe im gewählten Zeitraum</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Tier Edit Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTier?.isNew ? 'Neues Paket erstellen' : 'Paket bearbeiten'}
            </DialogTitle>
          </DialogHeader>
          
          {editingTier && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Paket-ID</Label>
                  <Input
                    value={editingTier.id}
                    onChange={(e) => setEditingTier({ ...editingTier, id: e.target.value })}
                    disabled={!editingTier.isNew}
                    placeholder="z.B. premium"
                  />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editingTier.name}
                    onChange={(e) => setEditingTier({ ...editingTier, name: e.target.value })}
                    placeholder="z.B. Premium"
                  />
                </div>
              </div>

              <div>
                <Label>Beschreibung</Label>
                <Input
                  value={editingTier.description}
                  onChange={(e) => setEditingTier({ ...editingTier, description: e.target.value })}
                  placeholder="Kurze Beschreibung des Pakets"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Provision (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    value={editingTier.commission_rate}
                    onChange={(e) => setEditingTier({ 
                      ...editingTier, 
                      commission_rate: parseFloat(e.target.value) || 0 
                    })}
                  />
                </div>
                <div>
                  <Label>Monatliche Gebühr (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.99"
                    value={editingTier.monthly_fee}
                    onChange={(e) => setEditingTier({ 
                      ...editingTier, 
                      monthly_fee: parseFloat(e.target.value) || 0 
                    })}
                  />
                </div>
                <div>
                  <Label>Max. Produkte</Label>
                  <Input
                    type="number"
                    min="-1"
                    value={editingTier.max_products}
                    onChange={(e) => setEditingTier({ 
                      ...editingTier, 
                      max_products: parseInt(e.target.value) || 0 
                    })}
                    placeholder="-1 = unbegrenzt"
                  />
                </div>
              </div>

              <div>
                <Label>Funktionen (eine pro Zeile)</Label>
                <Textarea
                  value={editingTier.features?.join('\n') || ''}
                  onChange={(e) => setEditingTier({ 
                    ...editingTier, 
                    features: e.target.value.split('\n').filter(f => f.trim()) 
                  })}
                  placeholder="Bis zu 50 Produkte&#10;Basis-Support&#10;Monatliche Auszahlung"
                  rows={5}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTier.can_customize_design}
                    onCheckedChange={(checked) => setEditingTier({ 
                      ...editingTier, 
                      can_customize_design: checked 
                    })}
                  />
                  <Label>Design anpassen</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTier.can_customize_colors}
                    onCheckedChange={(checked) => setEditingTier({ 
                      ...editingTier, 
                      can_customize_colors: checked 
                    })}
                  />
                  <Label>Farben anpassen</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTier.priority_support}
                    onCheckedChange={(checked) => setEditingTier({ 
                      ...editingTier, 
                      priority_support: checked 
                    })}
                  />
                  <Label>Prioritäts-Support</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveTier}>
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorSettings;
